/**
 * Estimated revenue for booking requests.
 *
 * Booking requests do not store dollar amounts — they are inquiries. To give
 * staff a revenue picture by booking type we value each request from the
 * published rate card. Everything produced here is an ESTIMATE and must be
 * labelled as such in the UI.
 */

export const RATES = {
  /** Lodge stay, per person per night. */
  lodgingPerPersonNight: 200_00,
  /** Rental boat, per boat per day. */
  boatPerDay: 400_00,
  /** Skiff, per skiff per day. */
  skiffPerDay: 100_00,
  /** Fish processing, approx. per box (~one box per angler). */
  processingPerBox: 150_00,
} as const;

export type BookingRow = {
  id: string;
  package: string | null;
  source: string | null;
  arrival_date: string | null;
  departure_date: string | null;
  party_size: number | null;
  notes: string | null;
  status: string | null;
  created_at: string;
};

export type BookingEstimate = {
  id: string;
  type: string;
  nights: number;
  anglers: number;
  boats: number;
  processing: boolean;
  lodgingCents: number;
  boatCents: number;
  processingCents: number;
  totalCents: number;
};

/** Requests in these states are not counted toward estimated revenue. */
export const NON_REVENUE_BOOKING_STATUSES = new Set([
  "cancelled",
  "canceled",
  "declined",
  "spam",
]);

const DAY = 86_400_000;

export function bookingType(row: Pick<BookingRow, "package" | "source">): string {
  const pkg = row.package?.trim();
  if (pkg) {
    // Older rows used "…Package" suffixes; normalise so they group together.
    return pkg.replace(/\s+package$/i, "");
  }
  return row.source === "contact" ? "General inquiry" : "Unspecified";
}

export function nightsBetween(arrival: string | null, departure: string | null): number {
  if (!arrival || !departure) return 0;
  const a = Date.parse(`${arrival}T00:00:00Z`);
  const d = Date.parse(`${departure}T00:00:00Z`);
  if (!Number.isFinite(a) || !Number.isFinite(d)) return 0;
  return Math.max(0, Math.round((d - a) / DAY));
}

/** Parses the "Boats: 2" / "Fish processing: Yes" lines the booking form writes. */
export function parseNotes(notes: string | null): { boats: number; processing: boolean } {
  const boatsMatch = notes?.match(/boats:\s*(\d+)/i);
  const procMatch = notes?.match(/fish processing:\s*(yes|no)/i);
  return {
    boats: boatsMatch ? Number(boatsMatch[1]) : 0,
    processing: (procMatch?.[1] ?? "").toLowerCase() === "yes",
  };
}

export function estimateBooking(row: BookingRow): BookingEstimate {
  const nights = nightsBetween(row.arrival_date, row.departure_date);
  const anglers = Math.max(0, row.party_size ?? 0);
  const { boats, processing } = parseNotes(row.notes);
  const days = Math.max(nights, boats > 0 ? 1 : 0);

  const lodgingCents = nights * anglers * RATES.lodgingPerPersonNight;
  const boatCents = boats * days * RATES.boatPerDay;
  const processingCents = processing ? Math.max(anglers, 1) * RATES.processingPerBox : 0;

  return {
    id: row.id,
    type: bookingType(row),
    nights,
    anglers,
    boats,
    processing,
    lodgingCents,
    boatCents,
    processingCents,
    totalCents: lodgingCents + boatCents + processingCents,
  };
}

export type BookingTypeTotals = {
  type: string;
  requests: number;
  lodgingCents: number;
  boatCents: number;
  processingCents: number;
  totalCents: number;
};

export function totalsByType(rows: BookingRow[]): BookingTypeTotals[] {
  const map = new Map<string, BookingTypeTotals>();
  for (const row of rows) {
    if (NON_REVENUE_BOOKING_STATUSES.has((row.status ?? "").toLowerCase())) continue;
    const est = estimateBooking(row);
    const entry = map.get(est.type) ?? {
      type: est.type,
      requests: 0,
      lodgingCents: 0,
      boatCents: 0,
      processingCents: 0,
      totalCents: 0,
    };
    entry.requests += 1;
    entry.lodgingCents += est.lodgingCents;
    entry.boatCents += est.boatCents;
    entry.processingCents += est.processingCents;
    entry.totalCents += est.totalCents;
    map.set(est.type, entry);
  }
  return [...map.values()].sort((a, b) => b.totalCents - a.totalCents || b.requests - a.requests);
}
